import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO'
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic'
import {PAdbContext} from '../PA.Core/PAdbContext'
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {WB_DataObjectColumnRow, WB_SourceTypeRow, WB_SourceTypeSTRRow} from "../PA.Core/PAdbSchemaRows"
import {IwbSourceType} from "../PA.Core/PAdbSchemaRowsIF"
import {esWB_DataObject_Column, esWB_SourceType, esWB_SourceTypeSTR} from "../PA.Core/PAdbSchema"
import {ESRecord} from "../PA.Core/lib/ESRecord"
import {ESDataStore} from "../PA.Core/lib/ESDataStore"
import {PAEnvironment} from "../PA.Core/PAEnvironment"
import {SourceTypeBOValidations} from "./SourceTypeBO.Validations"
import {CreateOptions, ESRecordResolver} from '../PA.Core/lib/ESRecordResolver'
import {ASync} from '../PA.Core/Basic/ASync'
import {Domain_getDefaultOrFirstItem} from "../PA.Core/PAdbUtils";


export class SourceTypeBO implements IBusinessObject {
    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    private drWB_SourceType: WB_SourceTypeRow

    constructor(options?: any) {

        this.CreateOptions = options || {}

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({id: 0, name: 'New Source Type'})

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = null
        this.LoadProgressProvider = new ProgressProvider('Loading', this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_SourceType = null
    }

    get WB_SourceType(): WB_SourceTypeRow {
        return this.drWB_SourceType
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        let self = this
        this.LoadProgressProvider.start("Creating Source Type")
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {}
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else
                self.dbContext = new PAdbContext()

            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext for new Source Type."
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            // Create record using dbContext
            self.dbContext.createRecordWithKey(esWB_SourceType)
                .then((newSourceTypeRow: WB_SourceTypeRow) => {
                    newSourceTypeRow.setDefaults()
                    self.drWB_SourceType = newSourceTypeRow
                    self.dbContext.add(esWB_SourceType, newSourceTypeRow)
                    self.Identity.setAll(self.WB_SourceType.cif.WB_SOURCETYPE_ID, self.WB_SourceType.cif.SOURCE_TYPE_ID, self.WB_SourceType.cif.SOURCE_TYPE_NAME)
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                    self.Identity.listenToRecordUpdates(self.WB_SourceType, 'WB_SOURCETYPE_ID', 'SOURCE_TYPE_ID', 'SOURCE_TYPE_NAME');
                    self.Identity.enableRecordWriteBack(self.WB_SourceType, undefined, 'SOURCE_TYPE_NAME')
                    self.Identity.enableRecordWriteBack(self.WB_SourceType, 'SOURCE_TYPE_ID', undefined)

                    self.LoadProgressProvider.finish()
                    self._bIsLoaded = true
                    self._OnLoadComplete = null
                    resolve(self)

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        })

        return this._OnLoadComplete
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this
        if (!this.bIsLoaded)
            this.Identity.setAll( anId, anId,'Loading Source Type (' + anId + ')')

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        let returnValue = self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")")

            if (self.bIsLoaded) {
                var sError = "Source Type already loaded."
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            self.LoadOptions = options || {}
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else
                self.dbContext = new PAdbContext()

            if (_.isEmpty(self.dbContext)) {
                let sError = "Unable to determine dbContext for new Source Type."
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = SourceTypeBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = SourceTypeBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let aSourceTypeRow: any = recordResolver.get(esWB_SourceType, anId)

                    if (_.isEmpty(aSourceTypeRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_SourceType = aSourceTypeRow;
                    self.Identity.setAll(self.WB_SourceType.cif.WB_SOURCETYPE_ID, self.WB_SourceType.cif.SOURCE_TYPE_ID, self.WB_SourceType.cif.SOURCE_TYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_SourceType, 'WB_SOURCETYPE_ID', 'SOURCE_TYPE_ID', 'SOURCE_TYPE_NAME')
                    self.Identity.enableRecordWriteBack(self.WB_SourceType, undefined, 'SOURCE_TYPE_NAME')
                    self.Identity.enableRecordWriteBack(self.WB_SourceType, 'SOURCE_TYPE_ID', undefined)
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });
        })

        return returnValue
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    async Validate(options?: any): Promise<IBusinessObject> {

        const self = this

        try {
            self.ValidateMessageProvider.clearMessages()
            self.ValidateProgressProvider.start("Validating " + self.WB_SourceType.cif.SOURCE_TYPE_NAME + "(" + self.WB_SourceType.cif.SOURCE_TYPE_ID + ")")


            self.ValidateOptions = options || {}

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Source Type."
                self.ValidateProgressProvider.exception(sError)
                throw new Error(sError)
            }

            await SourceTypeBOValidations.ValidateFields(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false
            } else {
                self._bIsValidated = true
            }
            self.ValidateProgressProvider.finish()
            return self

        } catch (err) {
            self._bIsValidated = false
            self.ValidateProgressProvider.exception(err)
            return self
        }
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this
        const wbSourceType = <IwbSourceType>self.drWB_SourceType
        self.SaveProgressProvider.start("Saving " + wbSourceType.SOURCE_TYPE_NAME + "(" + wbSourceType.SOURCE_TYPE_ID + ")")

        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // -- We could search the tree if the there is any record where store isn't set.
        try {
            if (self.dbContext == undefined) {
                self._bIsSaved = false
                var sError = "Unable to determine dbContext to Save Source Type."
                self.SaveProgressProvider.exception(sError)
                throw new Error(sError)
                return
            }

            self.SaveOptions = options || {}

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            // Add code to do individual BO save for dbContextRows....
            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false
                self.SaveProgressProvider.finish()
                return self
            }

            await saveFunction(esWB_SourceType, self.drWB_SourceType, {})
            await saveFunction(esWB_SourceTypeSTR, self.drWB_SourceType.WB_SourceTypeSTRRows, {})

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true
                })
            } else {
                self._bIsSaved = true
            }

            self.SaveProgressProvider.finish()
            return self
        } catch (err) {
            self.SaveProgressProvider.exception(err)
            return self
        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this
        var wbSourceType = <IwbSourceType>self.drWB_SourceType
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start("Deleting " + wbSourceType.SOURCE_TYPE_NAME + "(" + wbSourceType.WB_SOURCETYPE_ID + ")")
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the SourceType

            // 2. If Valid, mark all the records for deletion.
            self.drWB_SourceType.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true
                        self.DeleteProgressProvider.finish()
                        resolve(self)

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true
                self.DeleteProgressProvider.finish()
                resolve(self)
            }
        })
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    async addSourceTypeSTR(aSourceTypeSTRRowId?: number): Promise<WB_SourceTypeSTRRow> {
        const self = this


        let aNewRow: WB_SourceTypeSTRRow = <any>await self.dbContext.createRecordWithKey(esWB_SourceTypeSTR)
        aNewRow.setDefaults()
        aNewRow.cif.INTEGRATION_ALGORITHMS=(<any> await Domain_getDefaultOrFirstItem(self.dbContext, ['HarmonizationAlgorithm'], true)) || ''
        aNewRow.cif.WB_SOURCETYPE_ID = self.drWB_SourceType.cif.WB_SOURCETYPE_ID
        aNewRow.WB_SourceTypeRow = self.drWB_SourceType
        self.dbContext.add(esWB_SourceTypeSTR, aNewRow)

        return aNewRow
    }



    async removeSourceTypeSTR(aWbColumnId: number): Promise<WB_SourceTypeSTRRow> {
        const self = this

        for (let aSTSRow of self.drWB_SourceType.WB_SourceTypeSTRRows) {
            if (aSTSRow.isActive && aSTSRow.cif.WB_SOURCETYPESTR_ID == aWbColumnId) {
                aSTSRow.markDeleted()

                return aSTSRow
            }
        }

        return undefined
    }



    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const cc_options: CreateOptions = {
            standardConditions: {
            },
            dctIncludeBelongsToNamedRelations: {
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, cc_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_SourceType, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }


}
PAEnvironment.registerBusinessObjectClass(SourceTypeBO , "SourceTypeBO", "Source Type")
